import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAmplifyData } from '../hooks/useAmplifyData';
import { localSafetyFormService, LocalSafetyForm } from '../services/localDataService';
import { safetyFormService } from '../services/amplifyDataService';
import type { SafetyForm as SafetyFormType } from '../models/index.js';
import { SafetyForm as SafetyFormModel } from '../models/index.js';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { checkSafetyFormSyncStatus, SyncStatus } from '../utils/syncStatusChecker';
import './Screen.css';

interface PTPStats {
  total: number;
  draft: number;
  inProgress: number;
  completed: number;
  approved: number;
}

export default function PTPDashboard() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const isOnline = useNetworkStatus();
  
  // Use Amplify DataStore (Gen 1) - automatically handles offline sync
  const { data: amplifyForms, loading: amplifyLoading, error: amplifyError, refetch } = useAmplifyData<SafetyFormType>(SafetyFormModel);
  const [ptps, setPtps] = useState<LocalSafetyForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  
  // Function to fetch/refresh PTPs
  const fetchPTPs = async () => {
    try {
      // Try Amplify DataStore first (refetch is available from useAmplifyData hook)
      if (refetch) {
        await refetch();
      } else {
        // If no refetch, manually query
        const forms = await safetyFormService.getAll();
        setPtps(forms as any[]);
      }
      
      // Check sync status after refresh
      const status = await checkSafetyFormSyncStatus();
      setSyncStatus(status);
    } catch (error) {
      console.error('Error fetching PTPs from Amplify:', error);
      // Fallback to local service
      try {
        const localForms = await localSafetyFormService.getAll();
        setPtps(localForms);
      } catch (localError) {
        console.error('Local service also failed:', localError);
        setPtps([]);
      }
    }
  };
  
  // Check sync status when data changes
  useEffect(() => {
    if (amplifyForms && amplifyForms.length > 0) {
      checkSafetyFormSyncStatus().then(setSyncStatus).catch(console.error);
    }
  }, [amplifyForms]);
  
  // Debug: Log when amplifyForms changes
  useEffect(() => {
    console.log('📊 PTPDashboard: amplifyForms updated', {
      count: amplifyForms?.length ?? 0,
      forms: amplifyForms?.map((f: any) => ({
        id: f.id?.substring(0, 20),
        formType: f.formType,
        version: (f as any)._version,
      })) ?? [],
    });
  }, [amplifyForms]);
  
  // Transform Amplify SafetyForm to LocalSafetyForm format
  const transformAmplifyForm = (form: any): LocalSafetyForm => {
    return {
      id: form.id,
      formType: form.formType || 'PTP Form',
      projectName: form.projectName,
      location: form.location,
      workDate: form.workDate,
      foremanName: form.foremanName,
      foremanSignature: form.foremanSignature,
      requiredPermits: form.requiredPermits || [],
      requiredChecklist: form.requiredChecklist || [],
      ppe: form.ppe,
      hazards: form.hazards || [],
      controlMeasures: form.controlMeasures || [],
      tasks: form.tasks || [],
      emergencyActionPlan: form.emergencyActionPlan || false,
      emergencyMusterArea: form.emergencyMusterArea,
      emergencyContacts: form.emergencyContacts,
      teamMembers: form.teamMembers || [],
      crewSignatures: form.crewSignatures,
      nobodyHurtAcknowledgment: form.nobodyHurtAcknowledgment || false,
      nobodyHurtDate: form.nobodyHurtDate,
      supervisorStartSignature: form.supervisorStartSignature,
      supervisorStartDate: form.supervisorStartDate,
      supervisorStartPhone: form.supervisorStartPhone,
      endOfShiftReview: form.endOfShiftReview,
      superintendentSignature: form.superintendentSignature,
      superintendentReviewDate: form.superintendentReviewDate,
      supervisorEndPhone: form.supervisorEndPhone,
      superintendentEndPhone: form.superintendentEndPhone,
      status: form.status || 'draft',
      createdAt: form.createdAt,
      updatedAt: form.updatedAt,
    };
  };

  // Use Amplify data if available, otherwise fallback to local
  useEffect(() => {
    console.log('🔄 PTPDashboard useEffect - Data update:', {
      amplifyFormsLength: amplifyForms?.length ?? 0,
      amplifyLoading,
      amplifyError: amplifyError?.message,
    });

    if (amplifyForms !== undefined && amplifyForms !== null) {
      // Amplify DataStore is working - transform and use the data
      console.log('✅ Using Amplify DataStore data:', amplifyForms.length, 'items');
      const transformedForms = amplifyForms.map(transformAmplifyForm);
      console.log('✅ Transformed forms:', transformedForms.length);
      setPtps(transformedForms);
      setLoading(amplifyLoading);
    } else if (amplifyError) {
      // Amplify failed, try local service as fallback
      console.log('⚠️ Amplify DataStore error, falling back to local service:', amplifyError.message);
      localSafetyFormService.getAll()
        .then(localForms => {
          console.log('✅ Using local service data:', localForms.length, 'items');
          setPtps(localForms);
          setLoading(false);
        })
        .catch(err => {
          console.error('❌ Local service also failed:', err);
          setPtps([]);
          setLoading(false);
        });
    } else if (!amplifyLoading) {
      // Still loading, wait
      setLoading(true);
    }
  }, [amplifyForms, amplifyLoading, amplifyError]);
  
  // Filter PTPs
  const filteredPTPs = ptps.filter(ptp => {
    const matchesSearch = ptp.formType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ptp.status?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || ptp.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const stats: PTPStats = filteredPTPs.reduce((acc, ptp) => {
    acc.total++;
    const status = ptp.status || 'draft';
    if (status === 'draft') acc.draft++;
    else if (status === 'in_progress') acc.inProgress++;
    else if (status === 'completed') acc.completed++;
    else if (status === 'approved') acc.approved++;
    return acc;
  }, { total: 0, draft: 0, inProgress: 0, completed: 0, approved: 0 });

  const getStatusBadge = (status?: string) => {
    const statusMap: Record<string, { label: string; class: string }> = {
      draft: { label: 'Draft', class: 'badge-secondary' },
      in_progress: { label: 'In Progress', class: 'badge-warning' },
      completed: { label: 'Completed', class: 'badge-info' },
      approved: { label: 'Approved', class: 'badge-success' },
    };
    const s = status || 'draft';
    const badge = statusMap[s] || statusMap.draft;
    return <span className={`badge ${badge.class}`}>{badge.label}</span>;
  };

  return (
    <div className="screen">
      <div className="screen-header">
        <h1>Pre-Task Planning (PTP) Dashboard</h1>
        <p className="screen-subtitle">Manage and track all Pre-Task Planning forms</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid" style={{ marginBottom: '30px' }}>
        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <h3>Total PTPs</h3>
            <p className="stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-content">
            <h3>Draft</h3>
            <p className="stat-value">{stats.draft}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🔄</div>
          <div className="stat-content">
            <h3>In Progress</h3>
            <p className="stat-value">{stats.inProgress}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>Completed</h3>
            <p className="stat-value">{stats.completed}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✔️</div>
          <div className="stat-content">
            <h3>Approved</h3>
            <p className="stat-value">{stats.approved}</p>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="toolbar">
        <div className="toolbar-left">
          <input
            type="text"
            placeholder="Search PTPs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="approved">Approved</option>
          </select>
        </div>
        <div className="toolbar-right">
          <Link to="/ptp/create" className="btn btn-primary">
            ➕ Create New PTP
          </Link>
          <button className="btn btn-secondary" onClick={fetchPTPs}>
            🔄 Refresh
          </button>
          {syncStatus && syncStatus.pending > 0 && (
            <button 
              className="btn btn-info" 
              onClick={async () => {
                const { diagnoseSyncIssues } = await import('../utils/syncStatusChecker');
                await diagnoseSyncIssues();
              }}
              style={{ marginLeft: '8px' }}
            >
              🔍 Diagnose Sync
            </button>
          )}
        </div>
      </div>

      {/* PTP List */}
      {loading ? (
        <div className="loading">Loading PTPs...</div>
      ) : filteredPTPs.length === 0 ? (
        <div className="empty-state">
          <p>No PTPs found. Create your first Pre-Task Planning form to get started.</p>
          <Link to="/ptp/create" className="btn btn-primary">
            Create New PTP
          </Link>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Form Type</th>
                <th>Status</th>
                <th>Team Members</th>
                <th>Created</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPTPs.map((ptp) => (
                <tr key={ptp.id}>
                  <td>
                    <strong>{ptp.formType || 'PTP Form'}</strong>
                  </td>
                  <td>{getStatusBadge(ptp.status)}</td>
                  <td>
                    {ptp.teamMembers && ptp.teamMembers.length > 0
                      ? `${ptp.teamMembers.length} member(s)`
                      : 'No members'}
                  </td>
                  <td>
                    {ptp.createdAt
                      ? new Date(ptp.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td>
                    {ptp.updatedAt
                      ? new Date(ptp.updatedAt).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Link
                        to={`/ptp/${ptp.id}`}
                        className="btn btn-sm btn-primary"
                      >
                        View
                      </Link>
                      {ptp.status === 'draft' && (
                        <Link
                          to={`/ptp/${ptp.id}/edit`}
                          className="btn btn-sm btn-secondary"
                        >
                          Edit
                        </Link>
                      )}
                      {ptp.status === 'in_progress' && (
                        <Link
                          to={`/ptp/${ptp.id}/review`}
                          className="btn btn-sm btn-info"
                        >
                          Review
                        </Link>
                      )}
                      {ptp.status === 'completed' && (
                        <Link
                          to={`/ptp/${ptp.id}/approve`}
                          className="btn btn-sm btn-success"
                        >
                          Approve
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sync Status Warning */}
      {syncStatus && syncStatus.pending > 0 && (
        <div className="offline-notice" style={{ 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffc107',
          borderRadius: '8px',
          padding: '16px',
          marginTop: '20px',
          fontFamily: 'inherit',
          fontSize: '14px',
          lineHeight: '1.5',
          color: '#856404'
        }}>
          <strong style={{ fontSize: '14px', fontWeight: '600', display: 'block', marginBottom: '8px' }}>
            ⚠️ Sync Status:
          </strong>
          <span style={{ display: 'block', marginBottom: '8px' }}>
            {syncStatus.pending} of {syncStatus.total} PTPs are pending sync to DynamoDB.
            {!isOnline && ' You are offline - items will sync when connection is restored.'}
            {isOnline && ' Items should sync automatically. If they don\'t, check the browser console for errors.'}
          </span>
          <small style={{ 
            marginTop: '8px', 
            display: 'block', 
            fontSize: '13px',
            fontFamily: 'inherit',
            lineHeight: '1.4'
          }}>
            💡 Tip: Run <code style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.1)', 
              padding: '2px 6px', 
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '12px',
              fontWeight: '500'
            }}>checkSyncStatus()</code> in the browser console for detailed sync information.
          </small>
        </div>
      )}

      {/* Network Status */}
      {!isOnline && (
        <div className="offline-notice">
          ⚠️ You are offline. Changes will sync when connection is restored.
        </div>
      )}
    </div>
  );
}

